import { type KennelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import { type TableInformation } from '../../../config/flavourConfig';
import { type CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { type SingleTripCancellationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stc';
import { type SingleTripTravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stt';
import { type CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';

export const getCoverableWhoIsInsuredConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case 'Kennelforsikring':
            return getKennelWhoIsInsuredConfig(coverable);
        case 'Afbestillingsforsikring til enkeltrejse':
            return getSingleTripCancellationWhoIsInsuredConfig(coverable);
        case 'Enkeltrejseforsikring':
            return getSingleTripTravelWhoIsInsuredConfig(coverable);
        case 'Bilforsikring':
            return getPrivateCarWhoIsInsuredConfig(coverable as CarDTO, getHippoText);
        default:
            return undefined;
    }
};

const getKennelWhoIsInsuredConfig = (kennelCoverable: KennelDTO): TableInformation => {
    return {
        title: 'Hvem er forsikret?',
        rowsInformation: [
            {
                name: 'Antal hunde',
                fieldValue: kennelCoverable.numberOfDogs?.toString()
            }
        ]
    };
};

const getSingleTripCancellationWhoIsInsuredConfig = (
    stcCoverable: SingleTripCancellationDTO
): TableInformation => {
    return {
        title: 'Hvem er omfattet? ',
        rowsInformation: [
            {
                name: 'Antal personer',
                fieldValue: stcCoverable.numberOfInsuredPersons?.toString()
            }
        ]
    };
};

const getSingleTripTravelWhoIsInsuredConfig = (
    sttCoverable: SingleTripTravelDTO
): TableInformation => {
    const numOfPersonsUnder16: number = sttCoverable.numberOfPersonsUnder16 ?? 0;
    const numOfPersonsOver16: number = sttCoverable.numberOfPersonsOver16 ?? 0;
    const personsFieldValues = [
        {
            title: `Personer, der ved afrejsen er under 16 år: ${numOfPersonsUnder16}`
        },
        {
            title: `Personer, der ved afrejsen er over 16 år: ${numOfPersonsOver16}`
        }
    ];

    return {
        title: 'Hvem er omfattet? ',
        rowsInformation: [
            {
                name: 'Personer, som er dækket',
                fieldValue: personsFieldValues
            }
        ]
    };
};

const getPrivateCarWhoIsInsuredConfig = (
    carCoverable: CarDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: getHippoText(
            'DSS-COVERABLE-DETAILS-INFORMATION-ON-WHO-OWNS-AND-USES-THE-CAR-HEADER'
        ),
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-OWNER-TITLE'),
                fieldValue: carCoverable.primaryOwnerName
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-ADDRESS-OF-THE-OWNER-TITLE'),
                fieldValue: carCoverable.primaryOwnerAddress
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-PRIMARY-USER-TITLE'),
                fieldValue: carCoverable.primaryUserName,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'Primær bruger',
                        description: `Prisen er beregnet ud fra, at den, der benytter bilen fast og råder over den,
                                    er den person, der er nævnt i policen som primær bruger eller er denne persons
                                    samlever/ægtefælle, som bor på samme adresse. Du har pligt til at kontakteos,
                                    hvis den, der benytter bilen fast og råder over den, er en anden. Fx hvis det
                                    er en person, som ikke er nævnt på policen, hvis det er den sekundære bruger,
                                    eller hvis det er bilens ejer, og denne person ikke er den primære brugers ægtefælle/samlever.`
                    }
                }
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-PRIMARY-USER-ADDRESS'),
                fieldValue: carCoverable.primaryUserAddress
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-BIRTHDATE-PRIMARY-USER'),
                fieldValue: carCoverable.primaryUserDOB
            }
        ]
    };
};
